/**
 * Damped Least Squares (Levenberg-Marquardt) IK solver.
 *
 * The industry gold standard for multi-bone IK chains. Works directly in
 * joint-angle space by computing the Jacobian of the end-effector position
 * with respect to each joint's rotational degrees of freedom, then solving
 * for the minimum-energy angle update that moves the effector toward the target.
 *
 * Key advantages over position-space solvers:
 *   - Joint stiffness weights are mathematically natural (scale Jacobian columns)
 *   - Constraints applied per-iteration (solver adapts to constrained state)
 *   - Singularity-robust via damping parameter λ
 *   - Minimum-energy solutions (least total joint rotation → natural motion)
 *   - DOF-aware: respects hinge vs ball-and-socket joint types natively
 *
 * The solver computes:
 *   δθ = W · Jᵀ · (J·W·Jᵀ + λ²I)⁻¹ · e
 *
 * where J is the Jacobian, W the diagonal weight matrix, λ the damping
 * factor, and e the position error. The 3×3 matrix inversion is analytical.
 */

import * as THREE from "three/webgpu";
import { getRestPose } from "./boneRegistry.ts";
import { applyConstraint, getConstraint } from "./constraints.ts";
import type { IKChainState } from "./ikChainState.ts";
import type { Quat4 } from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Tuning constants
// ---------------------------------------------------------------------------

/** Damping factor λ. Higher → more stable near singularities but slower
 *  convergence. 0.5 balances stability and responsiveness. */
const DEFAULT_DAMPING = 0.5;

/** Maximum solver iterations. 20 is usually sufficient for constrained chains. */
const DEFAULT_ITERATIONS = 20;

/** Convergence tolerance (world units). */
const DEFAULT_TOLERANCE = 0.001;

// ---------------------------------------------------------------------------
// Scratch objects (avoid per-frame allocations in hot path)
// ---------------------------------------------------------------------------

const _error = new THREE.Vector3();
const _toEffector = new THREE.Vector3();
const _deltaQuat = new THREE.Quaternion();
const _parentWorld = new THREE.Quaternion();
const _parentInv = new THREE.Quaternion();
const _localDelta = new THREE.Quaternion();
const _localAxisVec = new THREE.Vector3();
const _boneWorldQ = new THREE.Quaternion();

// Pre-allocated FK arrays (grown once, reused across calls)
const _fkWorldPositions: THREE.Vector3[] = [];
const _fkWorldQuats: THREE.Quaternion[] = [];
const _boneOffsets: THREE.Vector3[] = [];

function ensureFKArrays(n: number): void {
  const posCount = n + 1;
  while (_fkWorldPositions.length < posCount)
    _fkWorldPositions.push(new THREE.Vector3());
  while (_fkWorldQuats.length < n) _fkWorldQuats.push(new THREE.Quaternion());
  while (_boneOffsets.length < n) _boneOffsets.push(new THREE.Vector3());
}

// Pre-allocated DOF arrays (sized for typical skeleton; grows if needed)
const MAX_DOFS = 64;
const _dofAxisX = new Float64Array(MAX_DOFS);
const _dofAxisY = new Float64Array(MAX_DOFS);
const _dofAxisZ = new Float64Array(MAX_DOFS);
const _dofJointIdx = new Int32Array(MAX_DOFS);
const _dofWeights = new Float64Array(MAX_DOFS);
const _J0 = new Float64Array(MAX_DOFS);
const _J1 = new Float64Array(MAX_DOFS);
const _J2 = new Float64Array(MAX_DOFS);
const _dTheta = new Float64Array(MAX_DOFS);

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface JacobianSolveOptions {
  /** Per-joint mobility weights (0 = locked, 1 = free). Defaults to all 1s. */
  jointWeights?: number[];
  /** Damping factor λ for DLS. Higher = more singularity-robust. Default 0.5. */
  damping?: number;
  /** Max iterations. Default 20. */
  iterations?: number;
  /** Convergence tolerance in world units. Default 0.001. */
  tolerance?: number;
  /** Mannequin object ID for rest-pose lookup (constraint application). */
  objectId?: string;
  /** Constraint blending weight (0 = skip constraints, 1 = full). Default 1. */
  constraintWeight?: number;
  /** Previous frame's solved quaternions for warm-starting continuity. */
  warmStartQuats?: ReadonlyMap<string, Quat4>;
  /** If true, exclude the last bone from DOFs (it's the dragged end effector). */
  excludeLastBone?: boolean;
}

// ---------------------------------------------------------------------------
// Solver
// ---------------------------------------------------------------------------

/**
 * Solve IK for a bone chain using Damped Least Squares.
 *
 * @param chain   Captured chain state (positions, lengths, quats).
 * @param target  Target world position for the chain tip (end effector).
 * @param options Solver options (weights, damping, constraints, warm-start).
 * @returns Map of boneName → local quaternion [x,y,z,w].
 */
export function solveJacobianIK(
  chain: IKChainState,
  target: THREE.Vector3,
  options: JacobianSolveOptions = {},
): Map<string, Quat4> {
  const {
    jointWeights,
    damping = DEFAULT_DAMPING,
    iterations = DEFAULT_ITERATIONS,
    tolerance = DEFAULT_TOLERANCE,
    objectId,
    constraintWeight = 1,
    warmStartQuats,
    excludeLastBone = false,
  } = options;

  const { boneNames, startLocalQuats, parentWorldQuats, positions } = chain;
  const n = boneNames.length;
  if (n === 0) return new Map();

  ensureFKArrays(n);

  // Clone local quaternions — modified iteratively during solve.
  // Warm-start from previous frame's solution when available.
  const localQuats: THREE.Quaternion[] = [];
  for (let i = 0; i < n; i++) {
    const ws = warmStartQuats?.get(boneNames[i]);
    if (ws) {
      localQuats.push(new THREE.Quaternion(ws[0], ws[1], ws[2], ws[3]));
    } else {
      localQuats.push(startLocalQuats[i].clone());
    }
  }

  // Compute structural bone offsets in bone-local space (constant).
  // offset_i = inv(boneWorldQuat_i) × (pos[i+1] − pos[i])
  for (let i = 0; i < n; i++) {
    _boneWorldQ.copy(parentWorldQuats[i]).multiply(startLocalQuats[i]);
    _boneWorldQ.invert();
    _boneOffsets[i]
      .subVectors(positions[i + 1], positions[i])
      .applyQuaternion(_boneWorldQ);
  }

  // Fixed reference frame (root bone's parent world quat) and root position.
  const chainParentWorldQuat = parentWorldQuats[0];
  const rootPos = positions[0];

  const lambda2 = damping * damping;

  // Number of bones included in DOFs
  const nDOF = excludeLastBone ? n - 1 : n;

  // --------------- Iterative solve ---------------
  for (let iter = 0; iter < iterations; iter++) {
    // 1. Forward kinematics: local quats → world positions + orientations
    _fkWorldPositions[0].copy(rootPos);
    _parentWorld.copy(chainParentWorldQuat);

    for (let i = 0; i < n; i++) {
      _fkWorldQuats[i].copy(_parentWorld).multiply(localQuats[i]);
      _fkWorldPositions[i + 1]
        .copy(_boneOffsets[i])
        .applyQuaternion(_fkWorldQuats[i])
        .add(_fkWorldPositions[i]);
      _parentWorld.copy(_fkWorldQuats[i]);
    }

    // 2. Error vector (target − current tip)
    _error.subVectors(target, _fkWorldPositions[n]);
    const errorMag = _error.length();
    if (errorMag < tolerance) break;

    // 3. Build Jacobian columns — one per DOF per joint
    let m = 0;
    for (let i = 0; i < nDOF; i++) {
      const w = jointWeights?.[i] ?? 1;
      if (w < 1e-6) continue; // effectively locked

      const constraint = getConstraint(boneNames[i]);

      // Vector from this joint to tip
      _toEffector.subVectors(_fkWorldPositions[n], _fkWorldPositions[i]);

      if (constraint?.type === "hinge") {
        // Single rotation axis (hinge joint)
        _localAxisVec
          .set(
            constraint.axis === "x" ? 1 : 0,
            constraint.axis === "y" ? 1 : 0,
            constraint.axis === "z" ? 1 : 0,
          )
          .applyQuaternion(_fkWorldQuats[i]);

        // J_col = axis × (effector − joint_pos)
        _dofAxisX[m] = _localAxisVec.x;
        _dofAxisY[m] = _localAxisVec.y;
        _dofAxisZ[m] = _localAxisVec.z;
        _J0[m] =
          _localAxisVec.y * _toEffector.z - _localAxisVec.z * _toEffector.y;
        _J1[m] =
          _localAxisVec.z * _toEffector.x - _localAxisVec.x * _toEffector.z;
        _J2[m] =
          _localAxisVec.x * _toEffector.y - _localAxisVec.y * _toEffector.x;
        _dofJointIdx[m] = i;
        _dofWeights[m] = w;
        m++;
      } else {
        // Ball-and-socket: 3 rotation axes (bone-local X, Y, Z → world)
        const axes: [number, number, number][] = [
          [1, 0, 0],
          [0, 1, 0],
          [0, 0, 1],
        ];
        for (const [ax, ay, az] of axes) {
          _localAxisVec.set(ax, ay, az).applyQuaternion(_fkWorldQuats[i]);

          _dofAxisX[m] = _localAxisVec.x;
          _dofAxisY[m] = _localAxisVec.y;
          _dofAxisZ[m] = _localAxisVec.z;
          _J0[m] =
            _localAxisVec.y * _toEffector.z - _localAxisVec.z * _toEffector.y;
          _J1[m] =
            _localAxisVec.z * _toEffector.x - _localAxisVec.x * _toEffector.z;
          _J2[m] =
            _localAxisVec.x * _toEffector.y - _localAxisVec.y * _toEffector.x;
          _dofJointIdx[m] = i;
          _dofWeights[m] = w;
          m++;
        }
      }
    }

    if (m === 0) break;

    // 4. Compute A = J·W·Jᵀ + λ²I (3×3 symmetric matrix)
    let a00 = lambda2,
      a01 = 0,
      a02 = 0;
    let a11 = lambda2,
      a12 = 0;
    let a22 = lambda2;

    for (let k = 0; k < m; k++) {
      const w = _dofWeights[k];
      const j0 = _J0[k],
        j1 = _J1[k],
        j2 = _J2[k];
      a00 += j0 * w * j0;
      a01 += j0 * w * j1;
      a02 += j0 * w * j2;
      a11 += j1 * w * j1;
      a12 += j1 * w * j2;
      a22 += j2 * w * j2;
    }

    // Symmetric: a10 = a01, a20 = a02, a21 = a12

    // 5. Analytical 3×3 inverse
    const det =
      a00 * (a11 * a22 - a12 * a12) -
      a01 * (a01 * a22 - a12 * a02) +
      a02 * (a01 * a12 - a11 * a02);

    if (Math.abs(det) < 1e-12) continue; // singular — skip iteration

    const invDet = 1 / det;
    const i00 = (a11 * a22 - a12 * a12) * invDet;
    const i01 = (a02 * a12 - a01 * a22) * invDet;
    const i02 = (a01 * a12 - a02 * a11) * invDet;
    const i11 = (a00 * a22 - a02 * a02) * invDet;
    const i12 = (a02 * a01 - a00 * a12) * invDet;
    const i22 = (a00 * a11 - a01 * a01) * invDet;

    // 6. v = A⁻¹ × e
    const v0 = i00 * _error.x + i01 * _error.y + i02 * _error.z;
    const v1 = i01 * _error.x + i11 * _error.y + i12 * _error.z;
    const v2 = i02 * _error.x + i12 * _error.y + i22 * _error.z;

    // 7. δθ = W × Jᵀ × v
    for (let k = 0; k < m; k++) {
      _dTheta[k] = _dofWeights[k] * (_J0[k] * v0 + _J1[k] * v1 + _J2[k] * v2);
    }

    // 8. Apply angle updates to local quaternions
    for (let k = 0; k < m; k++) {
      const angle = _dTheta[k];
      if (Math.abs(angle) < 1e-10) continue;

      const jointIdx = _dofJointIdx[k];

      // Recompute parent world quat from latest local quats
      _parentWorld.copy(chainParentWorldQuat);
      for (let j = 0; j < jointIdx; j++) {
        _parentWorld.multiply(localQuats[j]);
      }

      // World-space rotation delta: axisAngle(worldAxis, angle)
      const halfAngle = angle * 0.5;
      const sinHalf = Math.sin(halfAngle);
      _deltaQuat.set(
        _dofAxisX[k] * sinHalf,
        _dofAxisY[k] * sinHalf,
        _dofAxisZ[k] * sinHalf,
        Math.cos(halfAngle),
      );

      // Convert to local space: localDelta = inv(parent) × deltaWorld × parent
      _parentInv.copy(_parentWorld).invert();
      _localDelta.copy(_parentInv).multiply(_deltaQuat).multiply(_parentWorld);

      // Apply: newLocal = localDelta × oldLocal
      localQuats[jointIdx].premultiply(_localDelta);
      localQuats[jointIdx].normalize();
    }

    // 9. Enforce constraints per-iteration so the solver adapts to the
    //    feasible region. This is correct for chain-based DLS (short chains,
    //    large per-iteration steps) but NOT for whole-body DLS — see
    //    wholeBodyIK.ts for why that solver applies constraints post-solve.
    if (constraintWeight > 0 && objectId) {
      for (let i = 0; i < nDOF; i++) {
        const restQ = getRestPose(objectId, boneNames[i]);
        localQuats[i] = applyConstraint(
          boneNames[i],
          localQuats[i],
          restQ,
          constraintWeight,
        );
      }
    }
  }

  // Build result map
  const result = new Map<string, Quat4>();
  for (let i = 0; i < n; i++) {
    const q = localQuats[i];
    result.set(boneNames[i], [q.x, q.y, q.z, q.w]);
  }
  return result;
}
